import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Grid, Button, TextField,
  MenuItem, IconButton, Chip, Divider, Tooltip, InputAdornment,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Avatar, Autocomplete, Checkbox, FormControlLabel,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Receipt as BillIcon, Add as AddIcon, Delete as DeleteIcon,
  PlayArrow as ApplyIcon, Undo as ReverseIcon,
  Refresh as RefreshIcon, Save as SaveIcon,
  RemoveCircleOutline as RemoveRowIcon,
  PlaylistAdd as MoreRowsIcon, HistoryEdu as HistoryIcon,
  Close as CloseIcon, Tune as TuneIcon, Edit as EditIcon,
  Visibility as DetailIcon, Print as PrintIcon,
  CheckBox as CheckBoxIcon, CheckBoxOutlineBlank as CheckBoxBlankIcon,
  Lock as LockIcon,
  School as ScholarshipIcon,
  Assessment as ExamPrintIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import swal from '../../utils/swal';

const TERM_CHOICES = ['Term_1', 'Term_2', 'Term_3'];
const TYPE_CHOICES = [
  { value: 'all',     label: 'All students' },
  { value: 'Day',     label: 'Day students only' },
  { value: 'Boarder', label: 'Boarders only' },
];
const STATE_CHIP_COLOR = {
  pending:     'warning',
  virtualized: 'info',
  applied:     'success',
  reversed:    'default',
};
const STATE_LABEL = {
  pending:     'Pending',
  virtualized: 'Held (not committed)',
  applied:     'Applied',
  reversed:    'Reversed',
};

const fmt2 = (n) => Number(n || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

// One row inside a parent-account section. Kept to two columns —
// item label + amount — because the user found Narration + Code
// noise they never used, and Amount needed more room to breathe.
const blankItem = () => ({ item_label: '', amount: '' });

// One "section" = one parent account being billed for the shared
// (cla, term, acc_yer, student_type) bucket. The Prepare card now
// supports N sections so a single save can prepare, say, both
// Coding Classes Fee AND PTA in one shot.
let __sectionSeq = 0;
const nextSectionId = () => `s${++__sectionSeq}`;
const blankSection = () => ({
  id: nextSectionId(),
  parent_account: '',
  items: [blankItem(), blankItem(), blankItem()],
});

// ── Print HTML for the bill slip ──────────────────────────────
// One page per student, laid out to mirror the ICMS new_bill.jasper
// report: school header → student info block → four-column summary
// (Payment Account / Arrears / Terms Fees / Amount Due) → sub-items
// table with Total row → Bill Note. Arrears + Balance in the summary
// come from the backend, which reads the parent account's dynamic
// table live so pending bills print with today's outstanding.
const escHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

function buildBillReportHTML(payload) {
  const { bill, school, students } = payload;
  const sections = bill.sections || [];
  const termLabel = String(bill.term || '').replace('_', ' ');
  const reopens   = bill.reopening_date
    ? new Date(bill.reopening_date).toLocaleDateString('en-GH', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';
  const contactLine = [
    school.address && escHtml(school.address),
    school.phone   && `Tel: ${escHtml(school.phone)}`,
    school.email   && escHtml(school.email),
  ].filter(Boolean).join(' &nbsp;·&nbsp; ');

  // Preview banner shown on every slip when the backend used a fallback
  // roster (previous class in the ordering, because bill.cla is not yet
  // populated). Makes the origin obvious so no one mistakes a preview
  // slip for an applied one. Also shown for virtual holds.
  const previewBanner =
    payload.roster_source === 'previous_class_preview'
      ? `<div class="preview-banner preview-warn">
           <b>PREVIEW</b> — no students are physically in <b>${escHtml(bill.cla)}</b> yet.
           This roster was borrowed from <b>${escHtml(payload.roster_from_class || '—')}</b>
           (the previous class in the ordering). Each slip below shows the
           destination class the student would move into.
         </div>`
      : payload.roster_source === 'virtual_picks'
      ? `<div class="preview-banner preview-hold">
           <b>VIRTUAL HOLD</b> — this bill has not been committed to accounts yet.
           Fee rows will only land after the accountant clicks <b>Commit to accounts</b>.
         </div>`
      : '';

  // Grid of sub-item cards — one card per parent account. CSS grid
  // auto-wraps so 1-3 accounts fit in a single row on A4 without
  // spilling to a second page.
  const itemsBlock = sections.length ? `
    <div class="items-grid" style="--n:${Math.min(sections.length, 3)}">
      ${sections.map(sec => `
        <div class="acct">
          <div class="acct-h">
            <span class="acct-name">${escHtml(sec.parent_account.replace(/_/g, ' '))}</span>
            <span class="acct-sub">GHS ${fmt2(sec.subtotal)}</span>
          </div>
          <div class="acct-body">
            ${(sec.items || []).map(it => `
              <div class="li">
                <span class="li-lbl">${escHtml(it.item_label || it.narration || '')}</span>
                <span class="li-amt">${fmt2(it.amount)}</span>
              </div>`).join('')}
          </div>
        </div>`).join('')}
    </div>` : '';

  const noteBlock = bill.note ? `
    <div class="note">
      <div class="note-label">Bill Note</div>
      <div class="note-body">${escHtml(bill.note)}</div>
    </div>` : '';

  // Only render the Scholarship column when at least one student on this
  // bill has a non-zero picked discount — keeps the layout tight for the
  // (common) case where no scholarship is confirmed on the bill.
  const showScholarship = (students || []).some(s => Number(s.scholarship || 0) > 0);
  const pages = students.map((s, idx) => {
    const perAcct = s.per_account || [];
    const summaryRows = perAcct.map(row => `
      <tr>
        <td>${escHtml(row.parent_account.replace(/_/g, ' '))}</td>
        <td class="amt">${fmt2(row.arrears)}</td>
        <td class="amt">${fmt2(row.terms_fees)}</td>
        ${showScholarship ? `<td class="amt">${fmt2(row.scholarship || 0)}</td>` : ''}
        <td class="amt due">${fmt2(row.amount_due)}</td>
      </tr>`).join('');
    return `
    <section class="slip ${idx > 0 ? 'break' : ''}">
      ${school.logo
        ? `<img class="slip-watermark" src="${escHtml(school.logo)}" alt="" />`
        : `<div class="slip-watermark-text">${escHtml(school.school_name || 'STUDENT BILL')}</div>`}
      <div class="slip-content">
      ${previewBanner}
      <header class="masthead">
        <div class="mast-crest">${escHtml((school.school_name || 'S').trim().charAt(0).toUpperCase())}</div>
        <div class="mast-body">
          <div class="sc-name">${escHtml(school.school_name || 'School')}</div>
          ${school.motto ? `<div class="sc-motto">“${escHtml(school.motto)}”</div>` : ''}
          ${contactLine ? `<div class="sc-line">${contactLine}</div>` : ''}
        </div>
        ${school.logo ? `<img class="mast-logo" src="${escHtml(school.logo)}" alt="" />` : ''}
      </header>

      <div class="doctitle">
        <span class="doctitle-inner">
          <span class="dt-main">Student Bill</span>
          <span class="dt-sub">${escHtml(termLabel)} · ${escHtml(bill.acc_yer)}</span>
        </span>
      </div>

      <div class="info">
        <div class="info-cell"><span class="lbl">Student ID</span><span class="val">${escHtml(s.index_no)}</span></div>
        <div class="info-cell"><span class="lbl">Name</span><span class="val val-strong">${escHtml(s.name)}</span></div>
        <div class="info-cell info-right"><span class="lbl">Class</span><span class="val">${escHtml(s.cla)}</span></div>
        <div class="info-cell"><span class="lbl">Term</span><span class="val">${escHtml(termLabel)}</span></div>
        <div class="info-cell"><span class="lbl">Re-opening</span><span class="val">${escHtml(reopens || '—')}</span></div>
        <div class="info-cell info-right"><span class="lbl">Prepared By</span><span class="val">${escHtml(bill.prepared_by_name || '—')}</span></div>
      </div>

      <div class="section-h">Payment Summary</div>
      <table class="summary">
        <thead>
          <tr>
            <th>Payment Account</th>
            <th class="amt">Arrears</th>
            <th class="amt">Terms Fees</th>
            ${showScholarship ? '<th class="amt">Scholarship</th>' : ''}
            <th class="amt">Amount Due</th>
          </tr>
        </thead>
        <tbody>
          ${summaryRows}
        </tbody>
        <tfoot>
          <tr class="tot">
            <td>Total</td>
            <td class="amt">${fmt2(s.arrears)}</td>
            <td class="amt">${fmt2(s.terms_fees)}</td>
            ${showScholarship ? `<td class="amt">${fmt2(s.scholarship || 0)}</td>` : ''}
            <td class="amt due">${fmt2(s.amount_due)}</td>
          </tr>
        </tfoot>
      </table>

      <div class="grand">
        <span class="grand-lbl">Amount Payable</span>
        <span class="grand-amt">GHS ${fmt2(s.amount_due)}</span>
      </div>

      <div class="section-h">Sub-item Breakdown</div>
      ${itemsBlock || '<div class="empty">No sub-items on this bill.</div>'}

      ${noteBlock}

      <div class="foot">
        Bill Ref: <strong>#${escHtml(bill.id)}</strong>
        &nbsp;·&nbsp; Academic Year: ${escHtml(bill.acc_yer)}
        &nbsp;·&nbsp; Printed: ${new Date().toLocaleString('en-GH')}
      </div>
      </div>
    </section>`;
  }).join('');

  return `<!DOCTYPE html><html><head>
    <meta charset="utf-8" />
    <title>Student Bill — ${escHtml(bill.cla)} · ${escHtml(termLabel)}</title>
    <style>
      /* Print sizing — one student = one A4 sheet. Switch @page size to
         A5 in the browser print dialog if you want a smaller slip. */
      @page { size: A4; margin: 10mm 10mm; }
      * { box-sizing: border-box; }
      body {
        font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
        color: #1a1f2b; margin: 0; padding: 0; font-size: 11.5px;
        -webkit-print-color-adjust: exact; print-color-adjust: exact;
      }

      /* One slip = one page. page-break-after guarantees the next
         student starts on a fresh sheet. position:relative + overflow
         hidden anchor the watermark. */
      .slip {
        position: relative;
        padding: 8mm 9mm;
        page-break-after: always;
        page-break-inside: avoid;
        overflow: hidden;
      }
      .slip:last-of-type { page-break-after: auto; }

      /* Watermark — mirrors the receipt (position absolute, centered,
         low opacity, kept behind the content by z-index). The exact
         print-color-adjust keeps Chrome from stripping the faint image
         when printing. Text fallback used when the school has no logo. */
      .slip-watermark {
        position: absolute;
        top: 50%; left: 50%;
        transform: translate(-50%, -50%);
        width: 60%; max-width: 130mm; max-height: 130mm;
        object-fit: contain;
        opacity: 0.06;
        pointer-events: none;
        z-index: 0;
        user-select: none;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .slip-watermark-text {
        position: absolute;
        top: 50%; left: 50%;
        transform: translate(-50%, -50%) rotate(-24deg);
        font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
        font-size: 56px; font-weight: 900;
        color: #0f2a5c;
        opacity: 0.045;
        letter-spacing: 4px; text-transform: uppercase;
        text-align: center;
        width: 200mm;
        line-height: 1.05;
        pointer-events: none;
        z-index: 0;
        user-select: none;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .slip-content { position: relative; z-index: 1; }

      /* Masthead — crest + school block on a coloured band. */
      .masthead {
        display: flex; align-items: center; gap: 14px;
        padding: 10px 14px;
        background: linear-gradient(135deg, #0f2a5c 0%, #1c4b9f 100%);
        color: #fff; border-radius: 6px;
      }
      .mast-crest {
        width: 46px; height: 46px; border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        background: rgba(255,255,255,0.16);
        border: 2px solid rgba(255,255,255,0.55);
        font-size: 20px; font-weight: 800; letter-spacing: 0.5px;
      }
      .mast-body { flex: 1; min-width: 0; }
      .sc-name  { font-size: 17px; font-weight: 800; letter-spacing: 0.6px; text-transform: uppercase; line-height: 1.1; }
      .sc-motto { font-size: 10.5px; font-style: italic; opacity: 0.95; margin-top: 1px; }
      .sc-line  { font-size: 10.5px; opacity: 0.92; margin-top: 1px; }
      .mast-logo {
        width: 46px; height: 46px; object-fit: contain; flex-shrink: 0;
        background: rgba(255,255,255,0.95); border-radius: 6px; padding: 3px;
        border: 1px solid rgba(255,255,255,0.5);
      }

      /* Document title ribbon. */
      .doctitle { text-align: center; margin: 10px 0 8px; }
      .doctitle-inner {
        display: inline-flex; align-items: baseline; gap: 8px;
        padding: 4px 14px;
        border-top: 1px solid #c9a24a;
        border-bottom: 1px solid #c9a24a;
      }
      .dt-main {
        font-size: 14px; font-weight: 800; letter-spacing: 4px;
        text-transform: uppercase; color: #0f2a5c;
      }
      .dt-sub { font-size: 10.5px; color: #6b7280; letter-spacing: 1px; text-transform: uppercase; }

      /* Student info — 3-col grid, tight. */
      .info {
        display: grid; grid-template-columns: repeat(3, 1fr);
        gap: 4px 12px; margin: 6px 0 10px;
        background: #f7f9fc; border: 1px solid #e3e8f0; border-radius: 6px;
        padding: 8px 12px;
      }
      .info-cell { display: flex; flex-direction: column; }
      .info-cell.info-right { align-items: flex-end; text-align: right; }
      .info-cell .lbl { font-size: 9px; text-transform: uppercase; letter-spacing: 0.6px; color: #6b7280; font-weight: 700; }
      .info-cell .val { font-size: 11.5px; color: #1a1f2b; font-weight: 600; margin-top: 1px; }
      .info-cell .val-strong { font-weight: 800; }

      /* Section heading. */
      .section-h {
        margin: 8px 0 3px; font-size: 10px; font-weight: 800;
        text-transform: uppercase; letter-spacing: 1.2px;
        color: #0f2a5c;
        border-left: 3px solid #c9a24a; padding-left: 6px;
      }

      /* Summary table. */
      table.summary { width: 100%; border-collapse: separate; border-spacing: 0; margin-top: 2px; }
      .summary th, .summary td { padding: 6px 8px; font-size: 11px; }
      .summary thead th {
        background: #0f2a5c; color: #fff;
        font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.6px;
        text-align: left; font-weight: 700;
      }
      .summary thead th:first-child { border-top-left-radius: 5px; }
      .summary thead th:last-child  { border-top-right-radius: 5px; }
      .summary tbody td { border-bottom: 1px solid #e6ebf3; }
      .summary tbody tr:nth-child(even) td { background: #fafbfe; }
      .summary tfoot td {
        background: #eef3fb; font-weight: 800;
        border-top: 2px solid #0f2a5c;
      }
      .summary tfoot td.due { color: #0f2a5c; font-size: 12px; }
      .amt { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }

      /* Payable ribbon. */
      .grand {
        margin: 8px 0 4px;
        display: flex; align-items: center; justify-content: space-between;
        padding: 8px 14px;
        background: linear-gradient(90deg, #c9a24a 0%, #e0be6b 100%);
        color: #1a1f2b; border-radius: 6px;
      }
      .grand-lbl { font-size: 11px; font-weight: 800; letter-spacing: 1.5px; text-transform: uppercase; }
      .grand-amt { font-size: 16px; font-weight: 900; letter-spacing: 0.5px; }

      /* Sub-items grid — up to 3 columns, wraps neatly. */
      .items-grid {
        display: grid;
        grid-template-columns: repeat(var(--n, 3), 1fr);
        gap: 6px;
        margin-top: 2px;
      }
      .acct {
        border: 1px solid #dfe4ee; border-radius: 6px; overflow: hidden;
        background: #fff;
      }
      .acct-h {
        display: flex; align-items: center; justify-content: space-between;
        gap: 6px;
        background: #f0f4fb; padding: 5px 8px;
        border-bottom: 1px solid #dfe4ee;
      }
      .acct-name { font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.4px; color: #0f2a5c; }
      .acct-sub  { font-size: 10.5px; font-weight: 800; color: #0f2a5c; white-space: nowrap; }
      .acct-body { padding: 4px 8px 6px; }
      .li {
        display: flex; justify-content: space-between; gap: 8px;
        padding: 2px 0; font-size: 10.5px;
        border-bottom: 1px dotted #e6ebf3;
      }
      .li:last-child { border-bottom: 0; }
      .li-lbl { color: #1a1f2b; }
      .li-amt { color: #1a1f2b; font-weight: 700; font-variant-numeric: tabular-nums; white-space: nowrap; }

      .empty {
        margin-top: 4px; padding: 8px; text-align: center;
        color: #6b7280; font-size: 10.5px; font-style: italic;
        background: #f7f9fc; border: 1px dashed #d6dde8; border-radius: 6px;
      }

      /* Note. */
      .note {
        margin-top: 8px; padding: 6px 10px;
        background: #fffbe9; border: 1px solid #f0e4a8; border-radius: 6px;
      }
      .note-label { font-size: 9.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.6px; color: #6b6209; }
      .note-body  { font-size: 11px; margin-top: 1px; color: #4b4600; }

      /* Preview / hold banner — printed at the very top of every slip
         when the roster is a fallback preview (previous class) or when
         the bill is virtually held. Bold border so it stands out even
         on a photocopy. */
      .preview-banner {
        margin: 0 0 8px; padding: 6px 10px;
        border-radius: 6px; font-size: 10.5px; line-height: 1.35;
        border: 2px solid;
      }
      .preview-warn {
        background: #fff4e5; color: #7a3d00; border-color: #ff9800;
      }
      .preview-hold {
        background: #e3f2fd; color: #0d3c61; border-color: #1976d2;
      }

      /* Footer strip. */
      .foot {
        margin-top: 8px; padding-top: 5px;
        border-top: 1px dashed #d6dde8;
        text-align: center; font-size: 9px; color: #6b7280; letter-spacing: 0.3px;
      }
    </style>
  </head><body>${pages}</body></html>`;
}

const blankForm = (period) => ({
  classes:        [],                       // multi-select — fan out one bill per class on save
  term:           period?.term || 'Term_1',
  acc_yer:        period?.acc  || '',
  student_type:   'all',
  reopening_date: '',
  note:           '',
  sections:       [blankSection()],
});

export default function StudentBillsPage() {
  const [accounts, setAccounts] = useState([]);
  const [classes,  setClasses]  = useState([]);
  const [period,   setPeriod]   = useState(null);
  const [bills,    setBills]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [form,     setForm]     = useState(blankForm(null));
  // Master sub-item registry (the same `sub_item` table Admission
  // Details manages). Loaded once on page open so every row dropdown
  // is populated immediately, and refreshed whenever the Manage
  // Subaccounts dialog closes so newly added items show up right away.
  const [subItems, setSubItems] = useState([]);
  const [subOpen,  setSubOpen]  = useState(false);
  const globalItems = useMemo(() => subItems.map(s => s.name), [subItems]);

  // Detail dialog — shows the parent account, sub-items and amounts
  // for one saved bill. Loaded on demand from GET /:id so the row
  // itself can stay minimal.
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const openDetail = async (bill) => {
    setDetail({ ...bill, items: [] });
    setDetailLoading(true);
    try {
      const { data } = await API.get(`/student-bills/${bill.id}`);
      setDetail(data);
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not load bill details'));
      setDetail(null);
    } finally { setDetailLoading(false); }
  };
  const [filters,  setFilters]  = useState({ cla: '', term: '', acc_yer: '', state: '' });
  const [confirming, setConfirming] = useState(null);

  // Scholarship-on-this-bill dialog — lets the admin tick which of each
  // student's active scholarship enrollments should apply to THIS bill.
  // Persists to bill_scholarship_pick via /scholarship-picks and is read
  // back by the report / commit flow so the discount lands on both the
  // printed slip and the eventual live per-account row.
  const [scholarshipBill, setScholarshipBill] = useState(null);
  const openScholarshipPicker = (bill) => setScholarshipBill(bill);
  const closeScholarshipPicker = () => setScholarshipBill(null);

  const loadSubItems = async () => {
    try {
      const { data } = await API.get('/admission-details/sub-items');
      setSubItems(data || []);
    } catch { /* graceful — Autocomplete still accepts free text */ }
  };

  const loadLookups = async () => {
    try {
      const [a, c, s] = await Promise.all([
        API.get('/account-setup').catch(() => ({ data: [] })),
        API.get('/classes').catch(() => ({ data: [] })),
        API.get('/examination/settings').catch(() => ({ data: {} })),
      ]);
      const accts = (a.data || [])
        .filter(x => (x.source || 'stu_add') === 'stu_add')
        .map(x => x.account)
        .filter(Boolean);
      setAccounts(accts);
      setClasses((c.data || []).map(x => x.class || x.cla || x.name).filter(Boolean));
      setPeriod(s.data?.period || null);
      // Sub-item registry — same source as the Admission Details
      // "Manage Subaccounts" dialog, so newly added items surface
      // instantly in both places.
      loadSubItems();
    } catch (e) { /* graceful */ }
  };

  const loadBills = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.cla)            params.cla = filters.cla;
      if (filters.term)           params.term = filters.term;
      if (filters.acc_yer)        params.acc_yer = filters.acc_yer;
      if (filters.state)          params.state = filters.state;
      setBills((await API.get('/student-bills', { params })).data || []);
    } catch (e) { swal.error(swal.pickError(e, 'Could not load bills')); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadLookups(); }, []);
  useEffect(() => { loadBills(); /* eslint-disable-next-line */ }, [filters]);
  // Sync the form to the active period. Term defaults to whatever the
  // system says is active; academic year is locked to the active year
  // for Term 2 / Term 3 (mid-year top-ups). When the operator switches
  // to Term 1 the year unlocks — that's the rollover moment, and the
  // school needs to type the new academic year for the fresh cohort.
  useEffect(() => {
    if (!period) return;
    setForm(f => ({
      ...f,
      term:    f.term || period.term,
      acc_yer: f.term === 'Term_1' ? f.acc_yer : (period.acc || f.acc_yer),
    }));
    // eslint-disable-next-line
  }, [period]);
  // When the user changes the term itself, snap the year back to the
  // active period unless they've moved to Term 1 (rollover).
  useEffect(() => {
    if (!period) return;
    setForm(f => f.term === 'Term_1'
      ? f
      : { ...f, acc_yer: period.acc || f.acc_yer });
    // eslint-disable-next-line
  }, [form.term]);
  const yearLocked = form.term !== 'Term_1';

  // ── Section + item helpers ────────────────────────────────────
  const patchSection = (sid, patch) =>
    setForm(f => ({
      ...f,
      sections: f.sections.map(s => s.id === sid ? { ...s, ...patch } : s),
    }));
  const addSection = () =>
    setForm(f => ({ ...f, sections: [...f.sections, blankSection()] }));
  const removeSection = (sid) => {
    setForm(f => ({ ...f, sections: f.sections.filter(s => s.id !== sid) }));
  };
  const updateItem = (sid, i, patch) =>
    setForm(f => ({
      ...f,
      sections: f.sections.map(s => s.id !== sid ? s : ({
        ...s,
        items: s.items.map((r, j) => j === i ? { ...r, ...patch } : r),
      })),
    }));
  const addItem = (sid) =>
    setForm(f => ({
      ...f,
      sections: f.sections.map(s => s.id !== sid ? s : ({
        ...s, items: [...s.items, blankItem()],
      })),
    }));
  const removeItem = (sid, i) =>
    setForm(f => ({
      ...f,
      sections: f.sections.map(s => s.id !== sid ? s : ({
        ...s, items: s.items.filter((_, j) => j !== i),
      })),
    }));

  const sectionTotal = (section) =>
    (section.items || []).reduce((s, it) => s + (Number(it.amount) || 0), 0);
  const grandTotal = useMemo(
    () => form.sections.reduce((s, sec) => s + sectionTotal(sec), 0),
    [form.sections]
  );

  const resetForm = () => setForm(blankForm(period));

  // ── Save ────────────────────────────────────────────────────
  // ONE bill per (class, term, year, student type) bucket. Sections
  // are just parent-account groups INSIDE that one bill, so a single
  // POST creates the whole thing atomically — no more N separate bills
  // for School Fees + PTA + Coding Fee, no more per-section duplicate
  // errors either. Backend rejects the whole save if any account is
  // unknown so the form state stays consistent with the DB.
  const save = async () => {
    const classList = Array.isArray(form.classes) ? form.classes.filter(Boolean) : [];
    if (classList.length === 0) { swal.warning('Pick at least one class'); return; }
    if (!form.term)             { swal.warning('Pick a term'); return; }
    if (!form.acc_yer)          { swal.warning('Pick an academic year'); return; }

    const cleanedSections = form.sections
      .map(sec => ({
        parent_account: sec.parent_account,
        items: (sec.items || [])
          .filter(it => (it.item_label || '').trim())
          .map(it => ({
            item_label: it.item_label.trim(),
            amount:     Number(it.amount) || 0,
          })),
      }))
      .filter(x => x.parent_account && x.items.length > 0);

    if (cleanedSections.length === 0) {
      swal.warning('Pick at least one parent account and enter its sub-items before saving.');
      return;
    }

    // Guard: within a single parent account, sub-item labels must be
    // distinct. (Duplicates across DIFFERENT parent accounts are fine —
    // "Friday Wear" can live under both PTA and Coding Fee.)
    for (const sec of cleanedSections) {
      const seen = new Set();
      for (const it of sec.items) {
        const key = it.item_label.toLowerCase();
        if (seen.has(key)) {
          swal.warning(`"${it.item_label}" is listed twice under ${sec.parent_account.replace(/_/g, ' ')}. Each sub-item can only appear once per parent account.`);
          return;
        }
        seen.add(key);
      }
    }

    setSaving(true);
    // Fan out one POST per selected class so each class ends up with
    // its own bill row (the backend still enforces uniqueness on
    // (cla, term, acc_yer, student_type) per class). Collect per-class
    // results so the toast reports what succeeded and what didn't
    // instead of failing the whole batch on the first duplicate.
    const successes = [];
    const failures  = [];
    let totalAccounts = 0, totalItems = 0, totalAmount = 0;
    for (const cla of classList) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await API.post('/student-bills', {
          cla,
          term:           form.term,
          acc_yer:        form.acc_yer,
          student_type:   form.student_type,
          note:           form.note,
          reopening_date: form.reopening_date || null,
          sections:       cleanedSections,
        });
        successes.push(cla);
        totalAccounts += Number(data.account_count || 0);
        totalItems    += Number(data.item_count || 0);
        totalAmount   += Number(data.total_amount || 0);
      } catch (e) {
        failures.push({ cla, message: swal.pickError(e, 'Save failed') });
      }
    }
    setSaving(false);

    if (successes.length === 0) {
      const detail = failures.map(f => `• ${f.cla}: ${f.message}`).join('\n');
      swal.error(`No bills were saved.\n${detail}`);
      return;
    }

    const bits = [
      `Prepared bill${successes.length === 1 ? '' : 's'} for ${successes.length} class${successes.length === 1 ? '' : 'es'} (${successes.join(', ')}).`,
      `${totalAccounts} parent account row(s) · ${totalItems} sub-item(s) · GHS ${fmt2(totalAmount)}.`,
    ];
    if (failures.length > 0) {
      bits.push(`${failures.length} skipped: ${failures.map(f => `${f.cla} (${f.message})`).join('; ')}`);
      swal.warning(bits.join(' '));
    } else {
      swal.success(bits.join(' '));
    }
    resetForm();
    loadBills();
  };

  // ── Apply / Delete / Reverse ────────────────────────────────
  const applyBill = async (bill) => {
    // Fetch both the legacy scope-count and the new preflight (promotion
    // + scholarship inspection) in parallel — either failing is
    // non-fatal, apply will still surface a proper error if the actual
    // POST rejects.
    let preview = null, preflight = null;
    try {
      const [p1, p2] = await Promise.all([
        API.get('/student-bills/preview/scope', {
          params: { cla: bill.cla, student_type: bill.student_type },
        }).catch(() => null),
        API.get(`/student-bills/${bill.id}/apply-preflight`).catch(() => null),
      ]);
      preview   = p1?.data || null;
      preflight = p2?.data || null;
    } catch { /* pass */ }
    setConfirming({
      bill,
      preview,
      preflight,
      action: 'apply',
      // onConfirm accepts one of three shapes:
      //   null                          → current-class direct apply (original)
      //   Array (picks[])               → virtual/hold flow (state=virtualized)
      //   { promote?, auto_scholarship? }
      //                                 → new object flow. `promote:true`
      //                                   physically promotes previous-class
      //                                   students into this class first;
      //                                   `auto_scholarship:true` auto-picks
      //                                   every eligible enrollment. Either
      //                                   flag can be used alone.
      onConfirm: async (arg) => {
        try {
          let body = {};
          if (Array.isArray(arg))                body = { picks: arg };
          else if (arg && typeof arg === 'object') body = { ...arg };
          const { data } = await API.post(`/student-bills/${bill.id}/apply`, body);
          if (data.state === 'virtualized') {
            swal.success(
              `${data.message} You can now preview / print the bill sheets. Fee row writes will happen only after the accountant clicks "Commit to accounts".`
            );
          } else {
            const acctBits = (data.per_account || [])
              .map(a => `${a.parent_account.replace(/_/g, ' ')} · GHS ${fmt2(a.account_total)}`)
              .join(' · ');
            const bits = [];
            if (data.promotion && !data.promotion.skipped) {
              bits.push(`Promoted ${data.promotion.promoted} student(s) from ${data.promotion.from_classes.join(', ')} → ${bill.cla}.`);
            } else if (data.promotion?.already_promoted) {
              bits.push(data.promotion.reason);
            }
            if (data.scholarship?.picked_now > 0) {
              bits.push(`Auto-picked ${data.scholarship.picked_now} scholarship(s) (est. GHS ${fmt2(data.scholarship.estimated_total_discount || 0)}).`);
            }
            bits.push(`Applied to ${data.student_count} student(s) across ${data.per_account?.length || 0} parent account(s).`);
            bits.push(`Arrears carried: GHS ${fmt2(data.carried_arrears_total)}.`);
            if (data.fresh_accounts > 0) {
              bits.push(`${data.fresh_accounts} first-time account row(s) opened.`);
            }
            if (data.scholarship_total > 0) {
              bits.push(`Scholarship applied: GHS ${fmt2(data.scholarship_total)}.`);
            }
            if (acctBits) bits.push(`(${acctBits})`);
            swal.success(bits.join(' '));
          }
          setConfirming(null);
          loadBills();
        } catch (e) { swal.error(swal.pickError(e, 'Apply failed')); }
      },
    });
  };

  // Bulk-apply every pending bill for the currently-loaded term/year.
  // Term_1 bills auto-promote the previous class into their own before
  // applying (skipped if Examination already recorded the move).
  // Term_2/Term_3 bills apply straight through. Scholarships are
  // auto-picked when the admin confirms the second prompt.
  const applyAllPending = async () => {
    const pending = bills.filter(b => b.state === 'pending');
    if (pending.length === 0) {
      return swal.info('No pending bills to apply right now.');
    }
    const termsList = Array.from(new Set(pending.map(b => b.term))).map(t => (t || '').replace('_', ' ')).join(', ');
    const t1Count = pending.filter(b => String(b.term || '').toLowerCase() === 'term_1').length;
    const ok = await swal.confirm({
      title: `Apply all ${pending.length} pending bill(s)?`,
      text: `Terms in queue: ${termsList}.${t1Count > 0 ? ` ${t1Count} Term 1 bill(s) will auto-promote students from the class below first (skipped when Examination already recorded the move).` : ''} Arrears carry per student.`,
      tone: 'warning',
      confirmText: 'Yes, apply all',
    });
    if (!ok) return;

    // Second prompt — scholarship auto-application. Users almost always
    // want this on Term 1 (start of year) but ask explicitly so we
    // don't stamp discounts they weren't expecting.
    const withScholarship = await swal.confirm({
      title: 'Also apply the scholarship scheme?',
      text: 'For every student in these bills\' rosters who has an active scholarship enrollment, the matching picks will be auto-created before the bill applies — item-level targeting (e.g. Tuition 50%) is honoured.',
      tone: 'info',
      confirmText: 'Yes, apply scholarships',
      cancelText:  'No, skip',
    });

    try {
      const { data } = await API.post('/student-bills/apply-all-pending', {
        promote: true,
        auto_scholarship: !!withScholarship,
      });
      const parts = [data.message];
      const failures = (data.results || []).filter(r => !r.ok);
      if (failures.length) {
        parts.push(
          'Failed: ' + failures.slice(0, 3).map(f => `${f.cla} (${f.error})`).join('; ')
          + (failures.length > 3 ? ` …and ${failures.length - 3} more` : '')
        );
      }
      if (data.failed > 0) swal.warning(parts.join(' '));
      else swal.success(parts.join(' '));
      loadBills();
    } catch (e) { swal.error(swal.pickError(e, 'Bulk apply failed')); }
  };

  // Commit — turn a virtualized bill into an applied one. Fires the
  // actual fee-row writes on per-account tables. Confirm modal first
  // because this is destructive-ish (creates arrears, resets t_paid).
  const commitBill = async (bill) => {
    const ok = await swal.confirm({
      title: 'Commit bill to accounts?',
      text: `This will now write fee rows for ${bill.student_count} student(s) — arrears carry, terms fees post, and the bill becomes fully "Applied". Only run this after the students have taken their printed slips home.`,
      tone: 'warning',
      confirmText: 'Yes, commit',
    });
    if (!ok) return;
    try {
      const { data } = await API.post(`/student-bills/${bill.id}/commit`);
      const acctBits = (data.per_account || [])
        .map(a => `${a.parent_account.replace(/_/g, ' ')} · GHS ${fmt2(a.account_total)}`)
        .join(' · ');
      const bits = [
        data.message,
        `Arrears carried: GHS ${fmt2(data.carried_arrears_total)}.`,
      ];
      if (data.fresh_accounts > 0) bits.push(`${data.fresh_accounts} first-time account row(s) opened.`);
      if (acctBits) bits.push(`(${acctBits})`);
      swal.success(bits.join(' '));
      loadBills();
    } catch (e) { swal.error(swal.pickError(e, 'Commit failed')); }
  };

  const reverseBill = async (bill) => {
    const acctList = (bill.accounts || []).map(a => a.parent_account.replace(/_/g, ' ')).join(', ');
    const ok = await swal.confirm({
      title: 'Reverse applied bill?',
      text: `This deletes every fresh row this bill inserted (across ${acctList || 'each parent account'}) for ${bill.cla} · ${bill.term.replace('_', ' ')} · ${bill.acc_yer}. Only allowed when no payment has landed on those rows.`,
      tone: 'warning',
      confirmText: 'Yes, reverse',
    });
    if (!ok) return;
    try {
      const { data } = await API.post(`/student-bills/${bill.id}/reverse`);
      swal.success(data.message || 'Bill reversed');
      loadBills();
    } catch (e) { swal.error(swal.pickError(e, 'Reverse failed')); }
  };

  const deleteBill = async (bill) => {
    const ok = await swal.confirm({
      title: 'Delete pending bill?',
      text: `${bill.cla} · ${bill.term.replace('_', ' ')} · ${bill.acc_yer} · ${bill.account_count || 0} parent account(s). This can't be undone.`,
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await API.delete(`/student-bills/${bill.id}`);
      swal.success('Bill deleted');
      loadBills();
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  // Shared print entry point — both the default print button and the
  // "print from examination promotion" button hit the same /report
  // endpoint. The `rosterMode` param selects the roster source: '' for
  // the state-based default, 'exam' for the union of current-class +
  // previous-class students marked PROMOTED on their Term 3 remark.
  const openPrintWindow = async (bill, rosterMode) => {
    try {
      const { data } = await API.get(`/student-bills/${bill.id}/report`, {
        params: rosterMode ? { roster: rosterMode } : {},
      });
      if (!data?.students?.length) {
        if (rosterMode === 'exam') {
          swal.warning(
            `No students found for the examination-side promotion into ${bill.cla}. `
            + `Check that the class ordered just before ${bill.cla} has students, `
            + `and that their Term 3 remarks for the previous academic year aren't `
            + `set to REPEATED / WITHDRAWN / COMPLETED.`
          );
        } else {
          swal.warning(
            `No students match this bill's class + type — nothing to print. `
            + `If the year hasn't started yet, hold the bill virtually first `
            + `(Apply → "assume next class") to pick a roster.`
          );
        }
        return;
      }
      if (data.roster_source === 'previous_class_preview') {
        const ok = await swal.confirm({
          title: 'Preview from previous class',
          text:
            `No students are physically in ${bill.cla} yet, so this preview `
            + `is drawn from the previous class in the ordering (${data.roster_from_class}). `
            + `The slips will print each student's destination class as ${bill.cla}. `
            + `Continue?`,
          tone: 'warning',
          confirmText: 'Yes, print preview',
        });
        if (!ok) return;
      }
      if (data.roster_source === 'examination_promotion') {
        swal.info(
          `Examination-side promotion: ${data.students.length} student(s) taken from `
          + `${data.roster_from_class || '—'} — anyone not marked REPEATED / WITHDRAWN / `
          + `COMPLETED on their Term 3 remark. Physical class in the register is left `
          + `unchanged.`
        );
      }
      const html = buildBillReportHTML(data);
      const w = window.open('', '_blank', 'width=900,height=1000');
      if (!w) { swal.warning('Pop-up blocked — allow pop-ups for this site to print.'); return; }
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => { w.print(); }, 350);
    } catch (e) { swal.error(swal.pickError(e, 'Could not build report')); }
  };

  // Standard print — roster follows the bill's state (current / previous
  // class preview / virtual picks). Delegates to openPrintWindow which
  // is shared with the examination-promotion print variant so the two
  // paths stay in lock-step on pop-up / preview / HTML build.
  const printBillReport = (bill) => openPrintWindow(bill, '');
  const printBillFromExam = (bill) => openPrintWindow(bill, 'exam');

  // ── Render ──────────────────────────────────────────────────
  const periodReady = !!(period?.term && period?.acc);

  return (
    <Box sx={{ width: '100%', px: { xs: 1, md: 1.5 } }}>
      {/* Combined header — title on the left, active period on the right.
          Replaces the old stacked ModuleHeader + hero card so the form
          + saved-bills table sit higher on the viewport. */}
      <Card sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{
          background: (t) => periodReady
            ? `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`
            : `linear-gradient(135deg, ${t.palette.warning.main} 0%, ${t.palette.error.main} 100%)`,
          color: '#fff',
          px: { xs: 2, md: 3 }, py: 1.6,
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', rowGap: 1.2,
        }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)', width: 42, height: 42 }}>
            <BillIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
              Prepare Student Bill
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.92, display: 'block', mt: 0.2 }}>
              Register the term bill per class + parent account. Bills stay pending until you explicitly apply.
            </Typography>
          </Box>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: 'rgba(255,255,255,0.14)', px: 1.6, py: 0.9, borderRadius: 1.5,
          }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{
                opacity: 0.9, textTransform: 'uppercase', letterSpacing: 0.5,
                fontWeight: 800, fontSize: 9.5, display: 'block', lineHeight: 1,
              }}>
                Active Period
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2, mt: 0.2 }}>
                {periodReady
                  ? `${(period.term || '').replace('_', ' ')} · ${period.acc}`
                  : 'Not set'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Card>

      {/* Prepare + Saved Bills sit side-by-side on wide screens so the
          user isn't scrolling past the whole form to see what's been
          saved. They stack back to full width on narrower viewports. */}
      <Grid container spacing={2}>
      <Grid item xs={12} lg={6}>
      <Card sx={{ borderRadius: 2 }}>
        <SectionHeader icon={<AddIcon />} title="Prepare new bills"
          subtitle="One save can prepare bills across several parent accounts. All bills land in the same class · term · year · type bucket."
          right={
            <Tooltip title="Manage the master list of sub-items (same registry Admission Details uses)">
              <Button size="small" variant="outlined" startIcon={<TuneIcon />}
                onClick={() => setSubOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                Manage Subaccounts
              </Button>
            </Tooltip>
          } />
        <CardContent>
          {/* Shared bucket — 2 fields per row when the card is docked
              to the left half of the screen, still stacks on mobile. */}
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                options={classes}
                value={form.classes}
                onChange={(_, v) => setForm(f => ({ ...f, classes: v }))}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option}>
                    <Checkbox
                      icon={<CheckBoxBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 6 }}
                      checked={selected} />
                    {option}
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} size="small" {...getTagProps({ index })} key={option} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Classes"
                    placeholder={form.classes.length ? '' : 'Select one or more classes'}
                    helperText={form.classes.length > 1
                      ? `${form.classes.length} classes selected — the same bill will be prepared for each.`
                      : ' '} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Student type"
                value={form.student_type}
                onChange={e => setForm(f => ({ ...f, student_type: e.target.value }))}>
                {TYPE_CHOICES.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth size="small" label="Term"
                value={form.term}
                helperText={period?.term && form.term === period.term
                  ? `System active: ${period.term.replace('_', ' ')}`
                  : (form.term === 'Term_1'
                    ? 'Term 1 → academic year unlocked (new-year rollover)'
                    : 'Locked to the active academic year')}
                onChange={e => setForm(f => ({ ...f, term: e.target.value }))}>
                {TERM_CHOICES.map(t => (
                  <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Academic Year"
                placeholder="e.g. 2026/2027"
                value={form.acc_yer}
                disabled={yearLocked}
                helperText={yearLocked
                  ? `Follows active period · ${period?.acc || '—'}`
                  : 'Rollover — type the new academic year'}
                InputProps={yearLocked
                  ? { endAdornment: <InputAdornment position="end"><LockIcon fontSize="small" color="disabled" /></InputAdornment> }
                  : undefined}
                onChange={e => setForm(f => ({ ...f, acc_yer: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" type="date" label="Re-opens"
                InputLabelProps={{ shrink: true }}
                value={form.reopening_date}
                onChange={e => setForm(f => ({ ...f, reopening_date: e.target.value }))} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* One section per parent account */}
          <Stack spacing={2}>
            {form.sections.map((section, idx) => (
              <ParentAccountSection
                key={section.id}
                index={idx}
                section={section}
                accounts={accounts}
                usedAccounts={form.sections
                  .filter(s => s.id !== section.id)
                  .map(s => s.parent_account)
                  .filter(Boolean)}
                itemOptions={globalItems}
                canRemove={form.sections.length > 1}
                sectionTotal={sectionTotal(section)}
                onAccountChange={(pa) => patchSection(section.id, { parent_account: pa })}
                onAddItem={() => addItem(section.id)}
                onUpdateItem={(i, patch) => updateItem(section.id, i, patch)}
                onRemoveItem={(i) => removeItem(section.id, i)}
                onRemoveSection={() => removeSection(section.id)}
              />
            ))}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
            <Button size="small" variant="outlined" startIcon={<AddIcon />}
              onClick={addSection}
              sx={{ textTransform: 'none', fontWeight: 800 }}>
              Add another parent account
            </Button>
            <Box sx={{ flex: 1 }} />
            <Chip label={`Grand total: GHS ${fmt2(grandTotal)}`} color="primary"
              sx={{ fontWeight: 800 }} />
          </Stack>

          <TextField fullWidth multiline maxRows={4} size="small" label="Bill note (optional, shared across all bills)"
            sx={{ mt: 2 }}
            placeholder="Any narrative that should live on every bill in this save."
            value={form.note}
            onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={resetForm} sx={{ textTransform: 'none', fontWeight: 700 }}>
              Clear
            </Button>
            <Button variant="contained" onClick={save} disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              sx={{ textTransform: 'none', fontWeight: 800, minWidth: 220 }}>
              {saving
                ? 'Saving…'
                : `Save bill${form.classes.length > 1 ? 's' : ''}${form.classes.length ? ` · ${form.classes.length} class${form.classes.length === 1 ? '' : 'es'}` : ''} as pending`}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      </Grid>{/* /Prepare col */}

      {/* Saved Bills */}
      <Grid item xs={12} lg={6}>
      <Card sx={{ borderRadius: 2 }}>
        <SectionHeader icon={<HistoryIcon />} title="Saved bills"
          subtitle="Apply a pending bill to move student balances to arrears and bill the new term. Applied bills can be reversed only if no payment has landed."
          right={
            <Stack direction="row" spacing={0.5} alignItems="center">
              {bills.some(b => b.state === 'pending') && (
                <Tooltip title="Apply every pending bill in one shot. Term 1 bills auto-promote from the class below.">
                  <Button size="small" variant="contained" color="success"
                    onClick={applyAllPending}
                    startIcon={<ApplyIcon fontSize="small" />}
                    sx={{ textTransform: 'none', fontWeight: 800, py: 0.4 }}>
                    Apply all pending
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Refresh">
                <IconButton size="small" onClick={loadBills}><RefreshIcon fontSize="small" /></IconButton>
              </Tooltip>
            </Stack>
          } />

        <Box sx={{ px: 2, py: 1.4, display: 'flex', gap: 1, flexWrap: 'wrap', bgcolor: (t) => alpha(t.palette.primary.main, 0.03) }}>
          <TextField select size="small" label="Class" value={filters.cla}
            onChange={e => setFilters(f => ({ ...f, cla: e.target.value }))}
            sx={{ minWidth: 160 }}>
            <MenuItem value=""><em>All classes</em></MenuItem>
            {classes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Term" value={filters.term}
            onChange={e => setFilters(f => ({ ...f, term: e.target.value }))}
            sx={{ minWidth: 140 }}>
            <MenuItem value=""><em>All terms</em></MenuItem>
            {TERM_CHOICES.map(t => <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="State" value={filters.state}
            onChange={e => setFilters(f => ({ ...f, state: e.target.value }))}
            sx={{ minWidth: 140 }}>
            <MenuItem value=""><em>All</em></MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="applied">Applied</MenuItem>
            <MenuItem value="reversed">Reversed</MenuItem>
          </TextField>
        </Box>

        <TableContainer>
          {/* Compact 4-column layout — Class + term/type folded into a
              single Bucket cell, the parent account and its sub-items
              live in the Detail dialog behind the eye icon. Keeps the
              row clean when several bills exist for the same class.
              Actions column: up to 5 icons on some rows (view / scholarships
              / print / apply / delete), so the cell reduces its horizontal
              padding and shrinks IconButton padding to keep everything
              visible without the row scrolling horizontally. */}
          <Table size="small" sx={{
            '& td, & th': { px: 1.2 },
            tableLayout: 'fixed',
          }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 10.5, letterSpacing: 0.4, textTransform: 'uppercase' } }}>
                <TableCell>Bill</TableCell>
                <TableCell align="right" sx={{ width: 88 }}>Total</TableCell>
                <TableCell sx={{ width: 74 }}>State</TableCell>
                <TableCell align="right" sx={{ width: 224, px: 0.5 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && bills.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No bills yet. Prepare one on the left.
                  </TableCell>
                </TableRow>
              )}
              {!loading && bills.map(b => (
                <TableRow key={b.id} hover sx={{ '& td': { fontSize: 12.5, verticalAlign: 'top', py: 0.9 } }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 800, fontSize: 12.8 }}>
                      {b.cla}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                      {(b.term || '').replace('_', ' ')} · {b.student_type} · {b.account_count || (b.accounts?.length || 0)} account{(b.account_count || 0) === 1 ? '' : 's'} · {b.item_count} item{b.item_count === 1 ? '' : 's'}
                    </Typography>
                    {(b.accounts || []).length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.4 }}>
                        {b.accounts.map(a => (
                          <Chip key={a.parent_account} size="small" variant="outlined"
                            label={`${a.parent_account.replace(/_/g, ' ')} · ${fmt2(a.subtotal)}`}
                            sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    GHS {fmt2(b.total_amount)}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={STATE_CHIP_COLOR[b.state] || 'default'}
                      label={STATE_LABEL[b.state] || b.state}
                      sx={{ fontWeight: 700, height: 20 }} />
                  </TableCell>
                  {/* Actions cell — icons packed tight. `p: 0.35` on each
                      IconButton (~3px each side) plus `px: 0.5` on the cell
                      itself fits 5 icons in ~180px without truncation. */}
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', px: 0.5 }}>
                    <Tooltip title="View bill breakdown (parent account, sub-items)">
                      <IconButton size="small" color="primary" sx={{ p: 0.35 }} onClick={() => openDetail(b)}>
                        <DetailIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={
                      b.state === 'applied' || b.state === 'reversed'
                        ? 'View scholarships that were confirmed on this bill'
                        : 'Confirm which scholarships apply to this bill'
                    }>
                      <IconButton size="small" color="secondary" sx={{ p: 0.35 }} onClick={() => openScholarshipPicker(b)}>
                        <ScholarshipIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Print bill slips — one per student, with live arrears">
                      <IconButton size="small" color="info" sx={{ p: 0.35 }} onClick={() => printBillReport(b)}>
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Print using examination-side promotion (Term 3 remarks — PROMOTED)">
                      <IconButton size="small" color="info" sx={{ p: 0.35 }} onClick={() => printBillFromExam(b)}>
                        <ExamPrintIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {/* A reversed bill is offered again on purpose:
                        reverse wipes every row it wrote, so re-applying
                        starts from the same clean slate a first apply
                        would. Without this, undoing a bad apply left
                        the class permanently unbilled. */}
                    {(b.state === 'pending' || b.state === 'reversed') && (
                      <>
                        <Tooltip title={b.state === 'reversed'
                          ? 'Apply this bill again — the reversed rows are gone, so this re-bills the class cleanly'
                          : 'Apply bill — moves balances to arrears + bills new term'}>
                          <IconButton size="small" color="success" sx={{ p: 0.35 }} onClick={() => applyBill(b)}>
                            <ApplyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete pending bill">
                          <IconButton size="small" color="error" sx={{ p: 0.35 }} onClick={() => deleteBill(b)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {b.state === 'virtualized' && (
                      <>
                        <Tooltip title="Commit to accounts — writes the actual fee rows (arrears carry, new term bills)">
                          <IconButton size="small" color="success" sx={{ p: 0.35 }} onClick={() => commitBill(b)}>
                            <ApplyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel virtual hold (bill returns to pending, no rows written)">
                          <IconButton size="small" color="warning" sx={{ p: 0.35 }} onClick={() => reverseBill(b)}>
                            <ReverseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {b.state === 'applied' && (
                      <Tooltip title="Reverse this bill (only if no payments landed)">
                        <IconButton size="small" color="warning" sx={{ p: 0.35 }} onClick={() => reverseBill(b)}>
                          <ReverseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      </Grid>{/* /Saved bills col */}
      </Grid>{/* /Two-column row */}

      {/* Apply confirmation dialog */}
      <ApplyConfirmDialog
        open={!!confirming}
        onClose={() => setConfirming(null)}
        state={confirming} />

      {/* Master sub-item registry — same source as Admission Details.
          Refreshes globalItems on close so newly added entries appear
          in every row dropdown without a page reload. */}
      <SubItemRegistryDialog
        open={subOpen}
        onClose={() => setSubOpen(false)}
        items={subItems}
        reload={loadSubItems} />

      {/* Per-bill detail — parent account + sub-items breakdown */}
      <BillDetailDialog
        open={!!detail}
        onClose={() => setDetail(null)}
        loading={detailLoading}
        bill={detail} />

      {/* Scholarship-on-this-bill picker — parallels the virtual-move
          picker in spirit: admin ticks per-student × per-scheme, save
          persists via /scholarship-picks, and the report + commit reads
          the confirmed set. Frozen (read-only) after the bill is
          committed (applied / reversed). */}
      <ScholarshipPickerDialog
        bill={scholarshipBill}
        onClose={closeScholarshipPicker} />
    </Box>
  );
}

// ── One parent-account section inside the Prepare card ────────
// Header row: parent account picker (dropdown excludes accounts
// already chosen in another section), running section total, remove
// button. Below: sub-items grid — Item (Autocomplete pre-populated
// with EVERY registered sub-item in the system) and Amount only.
function ParentAccountSection({
  index, section, accounts, usedAccounts, itemOptions,
  canRemove, sectionTotal,
  onAccountChange, onAddItem, onUpdateItem, onRemoveItem, onRemoveSection,
}) {
  const opts = accounts.filter(a =>
    !usedAccounts.includes(a) || a === section.parent_account
  );

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{
        px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
        bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
        borderBottom: 1, borderColor: 'divider',
      }}>
        <Avatar sx={{ width: 26, height: 26, fontSize: 12, fontWeight: 800,
          bgcolor: 'primary.main' }}>{index + 1}</Avatar>
        <TextField select size="small" label="Parent Account"
          value={section.parent_account}
          onChange={e => onAccountChange(e.target.value)}
          sx={{ minWidth: 240 }}>
          {opts.length === 0 && <MenuItem disabled><em>No accounts left</em></MenuItem>}
          {opts.map(a => <MenuItem key={a} value={a}>{a.replace(/_/g, ' ')}</MenuItem>)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Chip label={`Total: GHS ${fmt2(sectionTotal)}`} color="primary"
          sx={{ fontWeight: 800 }} />
        <Tooltip title={canRemove ? 'Remove this parent account' : 'At least one section required'}>
          <span>
            <IconButton size="small" color="error"
              disabled={!canRemove}
              onClick={onRemoveSection}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11, letterSpacing: 0.4, textTransform: 'uppercase' } }}>
              <TableCell sx={{ width: '60%' }}>Sub-item</TableCell>
              <TableCell align="right" sx={{ width: '35%' }}>Amount</TableCell>
              <TableCell align="right" sx={{ width: 44 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {section.items.map((it, i) => {
              // Sub-item names must be unique within a section — a parent
              // account can't carry the same line twice. Options and the
              // free-text field are both validated: taken names from
              // OTHER rows in this section are hidden from the picker
              // and flagged if the operator hand-types the same label.
              const takenLower = new Set(
                section.items
                  .map((r, j) => j !== i ? String(r.item_label || '').trim().toLowerCase() : '')
                  .filter(Boolean)
              );
              const filteredOptions = itemOptions
                .filter(name => !takenLower.has(String(name).toLowerCase()));
              const currentLabel = String(it.item_label || '').trim();
              const isDup = currentLabel && takenLower.has(currentLabel.toLowerCase());
              return (
              <TableRow key={i}>
                <TableCell>
                  <Autocomplete freeSolo size="small"
                    options={filteredOptions}
                    value={it.item_label}
                    onChange={(_, v) => onUpdateItem(i, { item_label: String(v || '').trim() })}
                    onInputChange={(_, v) => onUpdateItem(i, { item_label: v })}
                    renderInput={(p) => (
                      <TextField {...p}
                        placeholder="Pick a sub-item or type a new one"
                        error={isDup}
                        helperText={isDup ? 'Already used in this account' : ' '} />
                    )} />
                </TableCell>
                <TableCell align="right">
                  <TextField size="small" fullWidth type="number"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">GHS</InputAdornment>,
                    }}
                    inputProps={{ style: { textAlign: 'right' }, min: 0, step: '0.01' }}
                    value={it.amount}
                    onChange={e => onUpdateItem(i, { amount: e.target.value })} />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={section.items.length > 1 ? 'Remove row' : 'At least one row'}>
                    <span>
                      <IconButton size="small" color="error"
                        disabled={section.items.length <= 1}
                        onClick={() => onRemoveItem(i)}>
                        <RemoveRowIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'divider',
        display: 'flex', justifyContent: 'flex-end' }}>
        <Button size="small" startIcon={<MoreRowsIcon />} onClick={onAddItem}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          Add sub-item
        </Button>
      </Box>
    </Box>
  );
}

// ── Section header used by both cards ─────────────────────────
function SectionHeader({ icon, title, subtitle, right }) {
  return (
    <Box sx={{
      px: 2, py: 1.4, display: 'flex', alignItems: 'center', gap: 1.2,
      borderBottom: 1, borderColor: 'divider',
      background: (t) => `linear-gradient(90deg, ${alpha(t.palette.primary.main, 0.08)} 0%, transparent 100%)`,
    }}>
      <Avatar sx={{ width: 34, height: 34,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.14),
        color: 'primary.main' }}>
        {icon}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontWeight={800}>{title}</Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {right}
    </Box>
  );
}

// ── Apply confirm dialog with preview + "carry arrears" warning ──
function ApplyConfirmDialog({ open, onClose, state }) {
  const { bill, preview, preflight, onConfirm } = state || {};
  const isTerm1 = String(bill?.term || '').toLowerCase() === 'term_1';

  // step: 'choice' (Term_1 only)  → 'roster' (virtual mode)
  //       'summary' (Term_2/3 or "no, current class" chosen)
  //       'promoteSummary' (physical promote + apply)
  const [step, setStep] = useState(isTerm1 ? 'choice' : 'summary');
  const [mode, setMode] = useState('current');     // 'current' | 'virtual' | 'promote'
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterErr, setRosterErr] = useState('');
  const [rosterInfo, setRosterInfo] = useState({ previous_class: '', students: [] });
  const [rosterPicks, setRosterPicks] = useState({});   // ind -> action ('promote'|'repeat'|'exclude')
  // Whether to also auto-apply the scholarship scheme on submit. Default
  // ON when the preflight says there are eligible enrollments — the
  // admin can uncheck if they want to hold discounts back.
  const [autoScholarship, setAutoScholarship] = useState(false);

  // Reset step + roster when the dialog opens or a new bill lands.
  useEffect(() => {
    if (!open) return;
    setStep(String(bill?.term || '').toLowerCase() === 'term_1' ? 'choice' : 'summary');
    setMode('current');
    setRosterInfo({ previous_class: '', students: [] });
    setRosterPicks({});
    setRosterErr('');
    setAutoScholarship((preflight?.scholarship?.eligible || 0) > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bill?.id, preflight?.scholarship?.eligible]);

  if (!open || !state) return null;

  const cnt = preview?.student_count;
  const accountList = (bill.accounts || [])
    .map(a => a.parent_account.replace(/_/g, ' '))
    .join(', ');

  const loadRoster = async (m) => {
    setRosterLoading(true); setRosterErr('');
    try {
      const { data } = await API.get(`/student-bills/${bill.id}/roster`, { params: { mode: m } });
      setRosterInfo({
        previous_class: data.previous_class || '',
        students: Array.isArray(data.students) ? data.students : [],
      });
      const seeded = {};
      for (const s of (data.students || [])) seeded[s.ind] = s.default_action || 'promote';
      setRosterPicks(seeded);
    } catch (e) {
      setRosterErr(e.response?.data?.message || 'Could not load roster');
    } finally { setRosterLoading(false); }
  };

  const pickVirtual = async () => {
    setMode('virtual');
    setStep('roster');
    await loadRoster('virtual');
  };
  const pickCurrent = () => {
    setMode('current');
    setStep('summary');
  };
  // Third path — physically promote the previous-class students into
  // bill.cla, then apply. Distinct from `virtual` because it changes
  // child_info.calss (via PromotionService) so the newly promoted
  // students receive the bill and their arrears carry with them.
  const pickPromote = () => {
    setMode('promote');
    setStep('promoteSummary');
  };
  const submitPromote = () => onConfirm({ promote: true, auto_scholarship: autoScholarship });

  const rosterPromoted = Object.values(rosterPicks).filter(v => v === 'promote').length;
  const rosterRepeated = Object.values(rosterPicks).filter(v => v === 'repeat').length;
  const rosterExcluded = Object.values(rosterPicks).filter(v => v === 'exclude').length;

  const setAction = (ind, action) => setRosterPicks(p => ({ ...p, [ind]: action }));
  const setAll    = (action) => {
    const next = {};
    for (const s of rosterInfo.students) next[s.ind] = action;
    setRosterPicks(next);
  };

  const submitCurrent  = () => onConfirm(autoScholarship ? { auto_scholarship: true } : null);
  const submitVirtual  = () => {
    const picks = rosterInfo.students.map(s => ({
      ind: s.ind, action: rosterPicks[s.ind] || 'promote',
    }));
    onConfirm(picks);
  };

  return (
    <Dialog open={open} onClose={onClose}
      maxWidth={step === 'roster' ? 'lg' : step === 'promoteSummary' ? 'md' : 'sm'} fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
        color: '#fff', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)' }}>
          <ApplyIcon />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
            {step === 'choice' ? 'First-term bill'
              : step === 'promoteSummary' ? 'Promote & apply'
              : 'Apply bill?'}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.92 }}>
            {bill.cla} · {(bill.term || '').replace('_', ' ')} · {bill.acc_yer}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>

      {/* ─────── STEP 1: Term 1 only — choose current vs. virtual next class ─────── */}
      {step === 'choice' && (
        <>
          <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This is a <b>Term 1</b> bill for <b>{bill.cla}</b>. At the start of a new academic
              year, the students who should receive it are usually the ones from the <b>previous
              class</b> (moving up), not the students physically in <b>{bill.cla}</b> right now.
            </Alert>
            <Stack spacing={1.5}>
              <Card variant="outlined"
                onClick={pickPromote}
                sx={{ p: 2, cursor: 'pointer', borderColor: 'success.main',
                  bgcolor: (t) => alpha(t.palette.success.main, 0.08),
                  '&:hover': { bgcolor: (t) => alpha(t.palette.success.main, 0.16) } }}>
                <Typography fontWeight={800} color="success.dark">
                  Yes — promote physically & apply
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Move every student from the previous class into <b>{bill.cla}</b>, then apply the
                  bill. Their <b>physical class changes</b> (child_info + all account tables cascade),
                  their arrears follow them, and they receive this Term 1 bill.
                  Students marked <b>REPEATED</b> in their previous Term 3 remark are skipped.
                  <br />Recommended when Examination has already staged promotions.
                </Typography>
              </Card>
              <Card variant="outlined"
                onClick={pickVirtual}
                sx={{ p: 2, cursor: 'pointer', borderColor: 'primary.main',
                  bgcolor: 'primary.50', '&:hover': { bgcolor: 'primary.100' } }}>
                <Typography fontWeight={800}>Yes — assume next class (hold virtually)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Load students from the class before {bill.cla}, pick Promote / Repeat / Exclude,
                  and print a preview slip. Physical class is <b>NOT</b> changed — fee rows land
                  only after the accountant clicks Commit.
                </Typography>
              </Card>
              <Card variant="outlined"
                onClick={pickCurrent}
                sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'grey.100' } }}>
                <Typography fontWeight={800}>No — use current {bill.cla} students</Typography>
                <Typography variant="caption" color="text.secondary">
                  Apply the bill to whoever is physically in {bill.cla} right now (original behaviour).
                </Typography>
              </Card>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
            <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
          </DialogActions>
        </>
      )}

      {/* ─────── STEP 2C: Promote physically + apply summary ─────── */}
      {step === 'promoteSummary' && (
        <>
          <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
            {/* Preflight-driven summary: shows what will happen based on
                the current DB state (already promoted vs. still to move,
                scholarship-eligible count and estimated discount). */}
            {preflight?.promotion_already && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <b>Promotion already recorded</b> — {preflight.promotion_already.count} student(s)
                were already promoted into <b>{bill.cla}</b> for {bill.term.replace('_', ' ')} {bill.acc_yer}
                {preflight.promotion_already.latest_date ? ` on ${preflight.promotion_already.latest_date}` : ''}.
                The system will <b>skip re-promoting</b> and apply the bill directly to those students.
              </Alert>
            )}
            {preflight && !preflight.promotion_already && preflight.previous_class_count > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <b>{preflight.previous_class_count} student(s)</b> will be promoted from{' '}
                <b>{preflight.previous_classes.join(', ')}</b> → <b>{bill.cla}</b>. Their
                <code>child_info.calss</code> and every per-account row is updated in one
                transaction; the audit is logged per student so any individual promotion
                can be reverted later. <b>REPEATED</b> Term 3 remarks are skipped.
              </Alert>
            )}
            {preflight && !preflight.promotion_already && preflight.previous_class_count === 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No students to promote from <b>{preflight.previous_classes.join(', ') || 'the class below'}</b>.
                {preflight.current_in_class > 0
                  ? ` The bill will apply to the ${preflight.current_in_class} student(s) currently in ${bill.cla}.`
                  : ' There are no students currently in this class either — apply will fail.'}
              </Alert>
            )}
            {preflight?.scholarship?.eligible > 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <b>{preflight.scholarship.eligible} student(s)</b> in this roster are enrolled in
                scholarship schemes that intersect this bill — estimated total discount{' '}
                <b>GHS {fmt2(preflight.scholarship.estimated_total_discount)}</b>.
                Toggle below to auto-apply.
              </Alert>
            )}
            {(bill.accounts || []).length > 0 && (
              <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {bill.accounts.map(a => (
                  <Chip key={a.parent_account} size="small" variant="outlined"
                    label={`${a.parent_account.replace(/_/g, ' ')} · GHS ${fmt2(a.subtotal)}`}
                    sx={{ fontWeight: 700 }} />
                ))}
              </Box>
            )}
            <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
              <Chip label={`Into · ${bill.cla}`} size="small" color="success" sx={{ fontWeight: 800 }} />
              <Chip label={`Type · ${bill.student_type}`} size="small" variant="outlined" sx={{ fontWeight: 700 }} />
              <Chip label={`Total · GHS ${fmt2(bill.total_amount)}`} size="small" color="primary" sx={{ fontWeight: 800 }} />
            </Stack>
            <FormControlLabel
              control={
                <Checkbox size="small"
                  checked={autoScholarship}
                  disabled={!preflight?.scholarship?.eligible}
                  onChange={(e) => setAutoScholarship(e.target.checked)} />
              }
              label={
                <Typography variant="body2" fontWeight={700}>
                  Also apply the scholarship scheme
                  {preflight?.scholarship?.eligible > 0 && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.8 }}>
                      ({preflight.scholarship.eligible} eligible · GHS {fmt2(preflight.scholarship.estimated_total_discount)})
                    </Typography>
                  )}
                  {preflight && !preflight.scholarship?.eligible && (
                    <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.8 }}>
                      (no eligible enrollments)
                    </Typography>
                  )}
                </Typography>
              }
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
            <Button onClick={() => setStep('choice')} sx={{ textTransform: 'none', mr: 'auto' }}>
              ← Back
            </Button>
            <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" color="success" onClick={submitPromote}
              startIcon={<ApplyIcon />}
              sx={{ textTransform: 'none', fontWeight: 800 }}>
              Promote & apply
            </Button>
          </DialogActions>
        </>
      )}

      {/* ─────── STEP 2A: Standard summary + confirm (current class) ─────── */}
      {step === 'summary' && (
        <>
          <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Applying is a one-time action. For every affected student, on EACH parent account in this bill{accountList ? ` (${accountList})` : ''}:
              <ul style={{ margin: '6px 0 0 18px' }}>
                <li>If they already have that account, their current outstanding balance moves to <b>arrears</b>.</li>
                <li>If they don't yet have that account, one is opened automatically — no arrears carried.</li>
                <li>New <b>Amount to Pay</b> for that account = its section subtotal on this bill.</li>
                <li>Paid resets to 0 for the new term.</li>
              </ul>
            </Alert>
            {(bill.accounts || []).length > 0 && (
              <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {bill.accounts.map(a => (
                  <Chip key={a.parent_account} size="small" variant="outlined"
                    label={`${a.parent_account.replace(/_/g, ' ')} · GHS ${fmt2(a.subtotal)}`}
                    sx={{ fontWeight: 700 }} />
                ))}
              </Box>
            )}
            <Stack direction="row" spacing={1.5} sx={{ mb: 1 }}>
              <Chip label={`Class · ${bill.cla}`} size="small" variant="outlined" sx={{ fontWeight: 700 }} />
              <Chip label={`Type · ${bill.student_type}`} size="small" variant="outlined" sx={{ fontWeight: 700 }} />
              <Chip label={`Total · GHS ${fmt2(bill.total_amount)}`} size="small" color="primary" sx={{ fontWeight: 800 }} />
            </Stack>
            {cnt != null && (
              <Alert severity={cnt > 0 ? 'success' : 'warning'}>
                {cnt > 0
                  ? `${cnt} student${cnt === 1 ? '' : 's'} match this class + type — apply will insert one fresh row per student per parent account.`
                  : 'No students match this class + type. Applying will do nothing.'}
              </Alert>
            )}
            {preflight?.scholarship?.eligible > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Alert severity="success" sx={{ mb: 1 }}>
                  <b>{preflight.scholarship.eligible} student(s)</b> in this roster have active
                  scholarship enrollments — estimated total discount{' '}
                  <b>GHS {fmt2(preflight.scholarship.estimated_total_discount)}</b>.
                </Alert>
                <FormControlLabel
                  control={
                    <Checkbox size="small"
                      checked={autoScholarship}
                      onChange={(e) => setAutoScholarship(e.target.checked)} />
                  }
                  label={
                    <Typography variant="body2" fontWeight={700}>
                      Also apply the scholarship scheme
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.8 }}>
                        ({preflight.scholarship.eligible} eligible · GHS {fmt2(preflight.scholarship.estimated_total_discount)})
                      </Typography>
                    </Typography>
                  }
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
            {isTerm1 && (
              <Button onClick={() => setStep('choice')} sx={{ textTransform: 'none', mr: 'auto' }}>
                ← Back
              </Button>
            )}
            <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" color="success" onClick={submitCurrent}
              startIcon={<ApplyIcon />}
              disabled={cnt === 0}
              sx={{ textTransform: 'none', fontWeight: 800 }}>
              Apply bill now
            </Button>
          </DialogActions>
        </>
      )}

      {/* ─────── STEP 2B: Roster picker (virtual mode) ─────── */}
      {step === 'roster' && (
        <>
          <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
            {rosterLoading && (
              <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
            )}
            {!rosterLoading && rosterErr && (
              <Alert severity="error">{rosterErr}</Alert>
            )}
            {!rosterLoading && !rosterErr && (
              <>
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  Roster from <b>{rosterInfo.previous_class || '—'}</b> — the class ordered just before <b>{bill.cla}</b>.
                  Defaults are pre-filled from each student's <b>Term 3 remark</b> for the previous year.
                  Only <b>Promoted</b> rows will appear on the printed bill. This action <b>does NOT write fee rows yet</b> —
                  the bill is held virtually until the accountant clicks <b>Commit to accounts</b>.
                  Physical class stays <b>unchanged</b>.
                </Alert>
                <Stack direction="row" spacing={0.8} sx={{ mb: 1, flexWrap: 'wrap' }}>
                  <Chip size="small" color="success" sx={{ fontWeight: 800 }} label={`${rosterPromoted} promoted`} />
                  <Chip size="small" color="warning" variant="outlined" sx={{ fontWeight: 800 }} label={`${rosterRepeated} repeated`} />
                  <Chip size="small" variant="outlined" sx={{ fontWeight: 800 }} label={`${rosterExcluded} excluded`} />
                  <Box sx={{ flex: 1 }} />
                  <Button size="small" onClick={() => setAll('promote')} sx={{ textTransform: 'none' }}>
                    All promoted
                  </Button>
                  <Button size="small" onClick={() => setAll('repeat')} sx={{ textTransform: 'none' }}>
                    All repeated
                  </Button>
                </Stack>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: '55vh', overflowY: 'auto' }}>
                  <Box component="table" sx={{
                    width: '100%', borderCollapse: 'collapse',
                    '& th': {
                      position: 'sticky', top: 0, zIndex: 1, bgcolor: 'grey.100',
                      textAlign: 'left', p: '8px 10px', fontSize: 11,
                      fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4,
                      color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider',
                    },
                    '& td': { p: '6px 10px', fontSize: 12.5, borderBottom: '1px solid', borderColor: 'rgba(0,0,0,0.05)' },
                    '& tr:hover td': { bgcolor: 'primary.50' },
                  }}>
                    <thead>
                      <tr>
                        <th style={{ width: 42 }}>#</th>
                        <th style={{ width: 120 }}>Index</th>
                        <th>Name</th>
                        <th style={{ width: 130 }}>Now in</th>
                        <th style={{ width: 220 }}>Action for this bill</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rosterInfo.students.length === 0 && (
                        <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#888' }}>
                          No students in {rosterInfo.previous_class || 'previous class'}.
                        </td></tr>
                      )}
                      {rosterInfo.students.map((s, i) => {
                        const action = rosterPicks[s.ind] || 'promote';
                        return (
                          <tr key={s.ind}>
                            <td style={{ color: '#888' }}>{i + 1}</td>
                            <td style={{ fontFamily: 'monospace' }}>{s.ind}</td>
                            <td style={{ fontWeight: 600 }}>{s.name}</td>
                            <td>{s.current_class}</td>
                            <td>
                              <TextField select size="small" fullWidth
                                value={action}
                                onChange={e => setAction(s.ind, e.target.value)}
                                SelectProps={{ sx: { fontSize: 12.5 } }}>
                                <MenuItem value="promote">Promoted → {bill.cla}</MenuItem>
                                <MenuItem value="repeat">Repeated (stay in {s.current_class})</MenuItem>
                                <MenuItem value="exclude">Exclude (withdrawn / transfer)</MenuItem>
                              </TextField>
                            </td>
                            <td style={{ color: '#888', fontSize: 11 }}>{s.note || ''}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Box>
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
            <Button onClick={() => setStep('choice')} sx={{ textTransform: 'none', mr: 'auto' }}>
              ← Back
            </Button>
            <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" color="info" onClick={submitVirtual}
              startIcon={<ApplyIcon />}
              disabled={rosterLoading || rosterPromoted === 0}
              sx={{ textTransform: 'none', fontWeight: 800 }}>
              Hold for {rosterPromoted} student{rosterPromoted === 1 ? '' : 's'} (print, not applied yet)
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

// ── Sub Item Registry dialog ───────────────────────────────────
// Mirrors the Admission Details "Manage Subaccounts" dialog exactly
// — same endpoints, same behaviour — so the one master list stays in
// sync across both pages.
// ── Bill Detail dialog ────────────────────────────────────────
// Shows a saved bill's parent-account sections and per-section sub-item
// breakdown. Each section is its own mini-table so the split is visually
// obvious — the bill as a whole is still ONE bill (one bucket), the
// sections just group the sub-items by which account they'll settle to.
function BillDetailDialog({ open, onClose, loading, bill }) {
  if (!open || !bill) return null;
  const sections = bill.sections || [];
  const grand = sections.reduce((s, sec) => s + Number(sec.subtotal || 0), 0);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
        color: '#fff', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)' }}>
          <BillIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
            {bill.cla} · {(bill.term || '').replace('_', ' ')} · {bill.acc_yer}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.92 }}>
            {bill.student_type} · {sections.length} parent account{sections.length === 1 ? '' : 's'} · GHS {fmt2(grand || bill.total_amount)}
          </Typography>
        </Box>
        <Chip size="small" label={bill.state}
          sx={{ fontWeight: 800, color: 'primary.main', bgcolor: '#fff', textTransform: 'capitalize' }} />
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>
      <DialogContent dividers sx={{ bgcolor: 'grey.50', p: 2 }}>
        {loading && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!loading && sections.length === 0 && (
          <Alert severity="info">No parent-account sections recorded for this bill.</Alert>
        )}
        {!loading && sections.length > 0 && (
          <Stack spacing={1.5}>
            {sections.map(sec => (
              <TableContainer key={sec.parent_account} component={Card} sx={{ borderRadius: 2 }}>
                <Box sx={{ px: 1.5, py: 0.8, display: 'flex', alignItems: 'center', gap: 1,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                  borderBottom: 1, borderColor: 'divider' }}>
                  <Typography sx={{ fontWeight: 800, flex: 1 }}>
                    {sec.parent_account.replace(/_/g, ' ')}
                  </Typography>
                  <Chip size="small" color="primary" label={`GHS ${fmt2(sec.subtotal)}`}
                    sx={{ fontWeight: 800, height: 20 }} />
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 10.5, letterSpacing: 0.4, textTransform: 'uppercase' } }}>
                      <TableCell>Sub-item</TableCell>
                      <TableCell align="right" sx={{ width: 140 }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sec.items.map(it => (
                      <TableRow key={it.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{it.item_label}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                          GHS {fmt2(it.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ))}
            <Box sx={{ px: 1.5, py: 1.2, borderRadius: 2, bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
              display: 'flex', alignItems: 'center', border: '2px solid',
              borderColor: 'primary.main' }}>
              <Typography sx={{ fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>
                Bill total
              </Typography>
              <Typography sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
                GHS {fmt2(grand || bill.total_amount)}
              </Typography>
            </Box>
          </Stack>
        )}
        {!loading && bill.note && (
          <Alert severity="info" sx={{ mt: 1.5 }} icon={false}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 800,
              textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
              Note
            </Typography>
            {bill.note}
          </Alert>
        )}
        {!loading && (bill.reopening_date || bill.prepared_at || bill.applied_at) && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
            {bill.reopening_date && (
              <Chip size="small" variant="outlined"
                label={`Re-opens: ${new Date(bill.reopening_date).toLocaleDateString('en-GH')}`} />
            )}
            {bill.prepared_at && (
              <Chip size="small" variant="outlined"
                label={`Prepared: ${new Date(bill.prepared_at).toLocaleDateString('en-GH')}`} />
            )}
            {bill.applied_at && (
              <Chip size="small" color="success"
                label={`Applied: ${new Date(bill.applied_at).toLocaleDateString('en-GH')} · ${bill.student_count} student(s)`} />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Scholarship-on-this-bill picker ─────────────────────────────
// Loads /scholarship-eligible for the selected bill and renders one
// row per (student, scheme) with a per-parent-account discount preview
// and an Include checkbox. Save posts /scholarship-picks and the
// bill's report / commit flow picks up the confirmed set from there.
//
// UX contract:
//   • pending / virtualized bills → checkboxes are editable, Save is enabled.
//   • applied / reversed bills    → read-only, shows the historical picks.
//   • bill with no scholarship-enrolled students on the roster is a
//     one-line empty state, not an error.
function ScholarshipPickerDialog({ bill, onClose }) {
  const open = !!bill;
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [payload, setPayload] = useState(null);
  const [picks,   setPicks]   = useState({});   // `${ind}::${scheme_id}` → true

  const readOnly = bill && (bill.state === 'applied' || bill.state === 'reversed');

  const load = async () => {
    if (!bill) return;
    setLoading(true); setError('');
    try {
      const { data } = await API.get(`/student-bills/${bill.id}/scholarship-eligible`);
      setPayload(data || null);
      const seeded = {};
      for (const c of (data?.candidates || [])) {
        if (c.already_picked) seeded[`${c.student_id}::${c.scheme_id}`] = true;
      }
      setPicks(seeded);
    } catch (e) {
      setError(e.response?.data?.message || 'Could not load scholarship candidates');
      setPayload(null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) { setPayload(null); setPicks({}); setError(''); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bill?.id]);

  if (!open) return null;

  const candidates = payload?.candidates || [];
  const setAll = (val) => {
    const next = {};
    if (val) {
      for (const c of candidates) {
        if (c.matches_bill) next[`${c.student_id}::${c.scheme_id}`] = true;
      }
    }
    setPicks(next);
  };
  const togglePick = (key) => setPicks(p => ({ ...p, [key]: !p[key] }));

  const pickedCount = Object.values(picks).filter(Boolean).length;
  const pickedDiscountTotal = candidates.reduce((sum, c) => {
    return picks[`${c.student_id}::${c.scheme_id}`]
      ? sum + Number(c.total_discount || 0)
      : sum;
  }, 0);

  const save = async () => {
    setSaving(true); setError('');
    try {
      const body = {
        picks: candidates
          .filter(c => picks[`${c.student_id}::${c.scheme_id}`])
          .map(c => ({ ind: c.student_id, scheme_id: c.scheme_id })),
      };
      const { data } = await API.post(`/student-bills/${bill.id}/scholarship-picks`, body);
      swal.success(data.message || 'Scholarship picks saved.');
      onClose();
    } catch (e) {
      setError(swal.pickError(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  const rosterSourceLabel = {
    current_class:           `current ${bill.cla}`,
    previous_class_preview:  `previous class preview (${payload?.roster_from_class || '—'})`,
    virtual_picks:           `virtual-hold picks (${payload?.roster_from_class || bill.cla})`,
  }[payload?.roster_source] || bill.cla;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose}
      maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.secondary.main} 0%, ${t.palette.primary.main} 100%)`,
        color: '#fff', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)' }}>
          <ScholarshipIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
            Scholarships on this bill
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.92 }}>
            {bill.cla} · {(bill.term || '').replace('_', ' ')} · {bill.acc_yer}
            &nbsp;·&nbsp; roster: {rosterSourceLabel}
          </Typography>
        </Box>
        <Chip size="small" label={STATE_LABEL[bill.state] || bill.state}
          sx={{ fontWeight: 800, color: 'primary.main', bgcolor: '#fff', textTransform: 'capitalize' }} />
        <IconButton onClick={onClose} sx={{ color: '#fff' }} disabled={saving}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
        <Alert severity={readOnly ? 'info' : 'warning'} sx={{ mb: 1.5 }}>
          {readOnly ? (
            <>
              This bill is <b>{STATE_LABEL[bill.state] || bill.state}</b> — the
              scholarship picks below are frozen. To change them, reverse the
              bill first, then re-open this dialog.
            </>
          ) : (
            <>
              Tick each enrollment that should apply to this bill. Ticked picks
              reduce the printed <b>Amount Due</b> and, when the bill is
              committed, stamp the discount into the student's live per-account
              row (<code>schola</code>). Untick to hold back a scholarship for a
              future bill. This dialog does <b>not</b> change the enrollment
              itself — only its use on this one bill.
            </>
          )}
        </Alert>

        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

        <Stack direction="row" spacing={0.8} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
          <Chip size="small" color="secondary"
            label={`${pickedCount} picked`} sx={{ fontWeight: 800 }} />
          <Chip size="small" variant="outlined"
            label={`Discount: GHS ${fmt2(pickedDiscountTotal)}`} sx={{ fontWeight: 800 }} />
          <Chip size="small" variant="outlined"
            label={`${candidates.length} eligible enrollment(s)`} sx={{ fontWeight: 700 }} />
          <Box sx={{ flex: 1 }} />
          {!readOnly && candidates.length > 0 && (
            <>
              <Button size="small" onClick={() => setAll(true)}
                disabled={saving}
                sx={{ textTransform: 'none' }}>
                Select all matching
              </Button>
              <Button size="small" onClick={() => setAll(false)}
                disabled={saving}
                sx={{ textTransform: 'none' }}>
                Clear all
              </Button>
            </>
          )}
        </Stack>

        {loading && (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
        )}
        {!loading && candidates.length === 0 && (
          <Alert severity="info">
            No students in this bill's roster are actively enrolled on any
            scholarship scheme, so there's nothing to confirm.
          </Alert>
        )}
        {!loading && candidates.length > 0 && (
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: '55vh', overflowY: 'auto', bgcolor: 'background.paper' }}>
            <Box component="table" sx={{
              width: '100%', borderCollapse: 'collapse',
              '& th': {
                position: 'sticky', top: 0, zIndex: 1, bgcolor: 'grey.100',
                textAlign: 'left', p: '8px 10px', fontSize: 11,
                fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4,
                color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider',
              },
              '& td': { p: '6px 10px', fontSize: 12.5, borderBottom: '1px solid', borderColor: 'rgba(0,0,0,0.05)', verticalAlign: 'top' },
              '& tr:hover td': { bgcolor: 'grey.50' },
            }}>
              <thead>
                <tr>
                  <th style={{ width: 42 }}>Pick</th>
                  <th style={{ width: 130 }}>Index</th>
                  <th>Student</th>
                  <th style={{ width: 90 }}>Class</th>
                  <th>Scheme</th>
                  <th>Per-account discount</th>
                  <th style={{ width: 100, textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => {
                  const key = `${c.student_id}::${c.scheme_id}`;
                  const checked = !!picks[key];
                  const disabled = readOnly || !c.matches_bill;
                  return (
                    <tr key={key} style={{ opacity: !c.matches_bill ? 0.55 : 1 }}>
                      <td>
                        <Checkbox size="small" checked={checked}
                          disabled={disabled}
                          onChange={() => togglePick(key)} />
                      </td>
                      <td style={{ fontFamily: 'monospace' }}>{c.student_id}</td>
                      <td style={{ fontWeight: 700 }}>{c.student_name}</td>
                      <td>{c.class_name || '—'}</td>
                      <td>{c.scheme_name}</td>
                      <td>
                        {c.per_account.filter(pa => pa.discount > 0).length === 0
                          ? <Typography variant="caption" color="text.secondary">
                              scheme has no lines matching this bill
                            </Typography>
                          : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                              {c.per_account.filter(pa => pa.discount > 0).map(pa => (
                                <Chip key={pa.parent_account} size="small" variant="outlined"
                                  label={`${pa.parent_account.replace(/_/g, ' ')} · GHS ${fmt2(pa.discount)}`}
                                  sx={{ fontWeight: 700, height: 20 }} />
                              ))}
                            </Box>
                          )}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                        GHS {fmt2(c.total_discount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={load} disabled={loading || saving}
          startIcon={<RefreshIcon fontSize="small" />}
          sx={{ textTransform: 'none', mr: 'auto' }}>
          Reload
        </Button>
        <Button onClick={onClose} disabled={saving}
          sx={{ textTransform: 'none' }}>
          Close
        </Button>
        {!readOnly && (
          <Button variant="contained" color="secondary" onClick={save}
            disabled={loading || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{ textTransform: 'none', fontWeight: 800 }}>
            {saving
              ? 'Saving…'
              : `Save ${pickedCount} pick${pickedCount === 1 ? '' : 's'}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function SubItemRegistryDialog({ open, onClose, items, reload }) {
  const [name,    setName]    = useState('');
  const [editing, setEditing] = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState('');

  // Reset local form state on every open so we don't carry a half-typed
  // name into a fresh session.
  useEffect(() => {
    if (open) { setName(''); setEditing(null); setError(''); }
  }, [open]);

  const submit = async () => {
    const clean = name.trim();
    if (!clean) return;
    setBusy(true); setError('');
    try {
      if (editing) {
        await API.put(`/admission-details/sub-items/${editing.trans_code}`, { name: clean });
      } else {
        await API.post('/admission-details/sub-items', { name: clean });
      }
      setName(''); setEditing(null);
      await reload();
    } catch (e) {
      setError(e.response?.data?.message || 'Save failed');
    } finally { setBusy(false); }
  };

  const remove = async (s) => {
    const ok = await swal.confirm({
      title: 'Delete sub-item?',
      text: `"${s.name}" will be removed from the registry. Bills that already reference it stay unchanged.`,
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await API.delete(`/admission-details/sub-items/${s.trans_code}`);
      if (editing?.trans_code === s.trans_code) { setEditing(null); setName(''); }
      await reload();
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      <DialogTitle sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2,
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TuneIcon />
          <Typography variant="h6" fontWeight={700}>Sub Item Registry</Typography>
        </Stack>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'background.default', p: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Card sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={1}>
            <TextField fullWidth size="small" autoFocus
              label={editing ? `Edit "${editing.name}"` : 'New sub-item name'}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              placeholder="e.g. Tuition" />
            <Button variant="contained" onClick={submit} disabled={busy || !name.trim()}
              startIcon={busy
                ? <CircularProgress size={14} color="inherit" />
                : (editing ? <SaveIcon /> : <AddIcon />)}>
              {editing ? 'Save' : 'Add'}
            </Button>
            {editing && (
              <Button onClick={() => { setEditing(null); setName(''); setError(''); }}>Cancel</Button>
            )}
          </Stack>
        </Card>

        <Card>
          {items.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.disabled">No sub-items yet</Typography>
            </Box>
          ) : (
            <Stack divider={<Divider />}>
              {items.map(s => (
                <Stack key={s.trans_code} direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>{s.name}</Typography>
                  <Tooltip title="Rename">
                    <IconButton size="small" color="primary"
                      onClick={() => { setEditing(s); setName(s.name); setError(''); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => remove(s)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          )}
        </Card>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, bgcolor: 'background.default' }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
